import React, { useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import { useFocusEffect } from "expo-router";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { BUDGET_CATEGORIES, CATEGORY_DISPLAY_NAMES } from "@/constants/MyMoney";
import {
  formatCurrency,
  formatCurrencyWhole,
  getCategoryEmoji,
  formatCentsToDisplay,
  parseDisplayToCents,
  handleCurrencyInputChange,
} from "@/utils/myMoney";
import useFinanceDataStore from "@/store/financeData";
import UserFinanceDataServices from "@/services/userFinanceDataServices";
import { CategoryBudget } from "@/types/financeData";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import FBTrackingService from "@/services/fbTrackingService";

// Edit icon SVG
const EditIcon = () => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
    <Path
      d="M16.474 5.408l2.118 2.117m-.756-3.982L12.109 9.27a2.118 2.118 0 00-.58 1.082L11 13l2.648-.53c.41-.082.786-.283 1.082-.579l5.727-5.727a1.853 1.853 0 10-2.621-2.621z"
      stroke="#161D14"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M19 15v3a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h3"
      stroke="#161D14"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function BudgetScreen() {
  const { financeData, isLoading, fetchFinanceData } = useFinanceDataStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editedBudgets, setEditedBudgets] = useState<Record<string, string>>(
    {}
  );
  const [focusedCategory, setFocusedCategory] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("my-money-budget");
    }, [])
  );

  // Get current budgets from finance data
  const budgets = useMemo(() => {
    const budgetMap: Record<string, CategoryBudget> = {};
    for (const budget of financeData?.analysis?.budgets || []) {
      budgetMap[budget.category] = budget;
    }
    return budgetMap;
  }, [financeData?.analysis?.budgets]);

  // Calculate totals
  const { totalBudget, totalSpent, remaining, spentPercent } = useMemo(() => {
    const totalBudget = financeData?.analysis?.totalBudget || 0;
    const totalSpent =
      financeData?.analysis?.budgets?.reduce(
        (sum, b) => sum + b.spentAmount,
        0
      ) || 0;
    const remaining = Math.max(0, totalBudget - totalSpent);
    const spentPercent =
      totalBudget > 0 ? Math.min(100, (totalSpent / totalBudget) * 100) : 0;
    return { totalBudget, totalSpent, remaining, spentPercent };
  }, [financeData?.analysis]);

  // Calculate edited total (convert display strings to cents)
  const editedTotal = useMemo(() => {
    let total = 0;
    for (const category of BUDGET_CATEGORIES) {
      if (editedBudgets[category] !== undefined) {
        total += parseDisplayToCents(editedBudgets[category]);
      } else {
        total += budgets[category]?.budgetAmount || 0;
      }
    }
    return total;
  }, [editedBudgets, budgets]);

  // Start editing - initialize with formatted display strings
  const handleStartEdit = useCallback(() => {
    const initialBudgets: Record<string, string> = {};
    for (const category of BUDGET_CATEGORIES) {
      const cents = budgets[category]?.budgetAmount || 0;
      initialBudgets[category] = formatCentsToDisplay(cents);
    }
    setEditedBudgets(initialBudgets);
    setIsEditing(true);
  }, [budgets]);

  // Cancel editing
  const handleCancel = useCallback(() => {
    setEditedBudgets({});
    setIsEditing(false);
    setFocusedCategory(null);
  }, []);

  // Save budgets
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const categories = BUDGET_CATEGORIES.map((category) => ({
        category,
        budgetAmount: parseDisplayToCents(editedBudgets[category] || "$0"),
      }));

      await UserFinanceDataServices.config({
        totalBudget: editedTotal,
        categories,
      });

      await fetchFinanceData({silent: true});
      setIsEditing(false);
      setEditedBudgets({});
      setFocusedCategory(null);
    } catch (error) {
      console.error("Error saving budgets:", error);
    } finally {
      setIsSaving(false);
    }
  }, [editedBudgets, editedTotal, fetchFinanceData]);

  // Handle input change with currency formatting
  const handleInputChange = useCallback((category: string, value: string) => {
    const formattedValue = handleCurrencyInputChange(value);
    setEditedBudgets((prev) => ({ ...prev, [category]: formattedValue }));
  }, []);

  // Get display value for a category (cents)
  const getCategoryValueCents = useCallback(
    (category: string): number => {
      if (isEditing && editedBudgets[category] !== undefined) {
        return parseDisplayToCents(editedBudgets[category]);
      }
      return budgets[category]?.budgetAmount || 0;
    },
    [isEditing, editedBudgets, budgets]
  );

  // Get display string for a category
  const getCategoryDisplayValue = useCallback(
    (category: string): string => {
      if (isEditing && editedBudgets[category] !== undefined) {
        return editedBudgets[category];
      }
      return formatCentsToDisplay(budgets[category]?.budgetAmount || 0);
    },
    [isEditing, editedBudgets, budgets]
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.content}>
      {/* Budget Summary Card */}
      <View style={styles.summaryCard}>
        <View style={styles.summaryHeader}>
          <Text style={styles.summaryTitle}>Budget</Text>
          <Text style={styles.summarySubtitle}>
            Adjust your spending targets by category. Your total updates
            automatically.
          </Text>
        </View>

        {/* Progress Bar */}
        <View style={styles.progressSection}>
          <View style={styles.progressBarContainer}>
            <View style={styles.progressBarBg} />
            <View
              style={[styles.progressBarFill, { width: `${spentPercent}%` }]}
            />
          </View>
          <View style={styles.progressLabels}>
            <Text style={styles.progressSpent}>
              {formatCurrencyWhole(totalSpent)} spent of{" "}
              {formatCurrencyWhole(totalBudget)}
            </Text>
            <Text style={styles.progressRemaining}>
              Remaining: {formatCurrencyWhole(remaining)}
            </Text>
          </View>
        </View>
      </View>

      {/* Category Budgets Card */}
      <View style={styles.categoriesCard}>
        {/* Header */}
        <View style={styles.categoryHeader}>
          <Text style={styles.categoryTitle}>Category</Text>
          {isEditing ? (
            <>
              <View style={styles.editActions}>
                <TouchableOpacity onPress={handleCancel} disabled={isSaving}>
                  <Text style={styles.cancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.saveButton}
                  onPress={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.saveText}>Save</Text>
                  )}
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <TouchableOpacity onPress={handleStartEdit}>
                <EditIcon />
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* Category List */}
        <View style={styles.categoryList}>
          {BUDGET_CATEGORIES.map((category) => {
            const valueCents = getCategoryValueCents(category);
            const displayValue = getCategoryDisplayValue(category);
            const isZero = valueCents === 0;
            const isFocused = focusedCategory === category;

            return (
              <View key={category} style={styles.categoryRow}>
                <Text style={styles.categoryName}>
                  {getCategoryEmoji(category)}{" "}
                  {CATEGORY_DISPLAY_NAMES[category]}
                </Text>
                {isEditing ? (
                  <View
                    style={[
                      styles.inputContainer,
                      isFocused && styles.inputContainerFocused,
                    ]}
                  >
                    <TextInput
                      style={[
                        styles.input,
                        isZero && !isFocused && styles.inputZero,
                      ]}
                      value={displayValue}
                      onChangeText={(text) => handleInputChange(category, text)}
                      onFocus={() => setFocusedCategory(category)}
                      onBlur={() => setFocusedCategory(null)}
                      keyboardType="decimal-pad"
                      selectTextOnFocus
                    />
                  </View>
                ) : (
                  <Text
                    style={[styles.categoryValue, isZero && styles.valueZero]}
                  >
                    {valueCents % 100 === 0
                      ? formatCurrencyWhole(valueCents)
                      : formatCurrency(valueCents)}
                  </Text>
                )}
              </View>
            );
          })}
        </View>

        {/* Total */}
        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>Total monthly budget</Text>
          <Text style={styles.totalValue}>
            {formatCurrencyWhole(isEditing ? editedTotal : totalBudget)}
          </Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 24,
    paddingBottom: 100,
    width: SCREEN_WIDTH,
    alignSelf: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.background,
  },
  // Summary Card
  summaryCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    gap: 20,
    ...Shadows.card,
  },
  summaryHeader: {
    gap: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
    paddingBottom: 8,
  },
  summaryTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  summarySubtitle: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  progressSection: {
    gap: 4,
  },
  progressBarContainer: {
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarBg: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#D9D9D9",
    borderRadius: 4,
  },
  progressBarFill: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: "#C12915",
    borderRadius: 4,
  },
  progressLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  progressSpent: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  progressRemaining: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    textAlign: "right",
  },
  // Categories Card
  categoriesCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    ...Shadows.card,
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
    paddingBottom: 8,
  },
  categoryTitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  editActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  cancelText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  saveButton: {
    backgroundColor: "#0F9129",
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 100,
    width: 80,
    alignItems: "center",
    justifyContent: "center",
    height: 31,
  },
  saveText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#FFFFFF",
  },
  categoryList: {
    marginTop: 20,
    gap: 20,
  },
  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  categoryName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#000000",
    flex: 1,
  },
  categoryValue: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0E3115",
    textAlign: "right",
  },
  valueZero: {
    color: "#A3A3A3",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 4,
    paddingHorizontal: 12,
    height: 32,
  },
  inputContainerFocused: {
    borderColor: "#0F9129",
  },
  input: {
    width: 100,
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0E3115",
    textAlign: "right",
    padding: 0,
  },
  inputZero: {
    color: "#A3A3A3",
  },
  totalSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
    paddingTop: 12,
    marginTop: 20,
  },
  totalLabel: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#A3A3A3",
  },
  totalValue: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0E3115",
  },
});
